'use client';

import { createContext, useContext } from 'react';
import type { Widget } from '@parkingbase/ecommerce-api-dev';

import type { ReservationMode } from '@/components/reserve/search-bar/SearchBar.types';

import type { HeroSearchProps } from '@/components/landing/hero-search';
import type { ActiveParkingProps } from '@/components/landing/active-parking/ActiveParking.types';
import type { ParkAgainItem } from '@/components/landing/park-again/ParkAgain.types';
import type { SitePageSourceView } from './SitePage.types';

/** What the NextReservation block needs, already display-ready. */
export interface NextReservationBlockProps {
  garageName: string;
  timeText: string;
}

/**
 * Everything `useLandingPage()` computes for the landing blocks. `page.tsx` builds it once and
 * the blocks stay dumb adapters onto the existing landing components.
 */
export interface LandingBlocksValue {
  /** Search-card props for `HeroSearch` (no widget/media/copy — the HERO block adds those). */
  heroProps: Omit<
    HeroSearchProps,
    | 'widget'
    | 'backgroundVideoUrl'
    | 'backgroundImageUrl'
    | 'blockMode'
    | 'headline'
    | 'subheadline'
  >;
  backgroundVideoUrl?: string | null;
  backgroundImageUrl?: string | null;
  onSignup: () => void;
  personal: {
    activeParkingProps:
      | Omit<
          ActiveParkingProps,
          | 'onExtend'
          | 'onPay'
          | 'onView'
          | 'onViewAll'
          | 'showFindSession'
          | 'onFindSession'
          | 'className'
        >
      | null
      | undefined;
    canViewParkingPass: boolean;
    showFindSession: boolean;
    nextReservationProps: NextReservationBlockProps | null | undefined;
    parkAgainItems: ParkAgainItem[];
    onViewAll: () => void;
    onViewParking: () => void;
    onExtendParking: () => void;
    onPayParking: () => void;
    onFindSession: () => void;
    onViewAllReservations: () => void;
    onSelectReservation: () => void;
    onViewAllParkAgain: () => void;
  };
}

export const LandingBlocksContext = createContext<LandingBlocksValue | null>(null);

/** Throws when missing: a block rendered outside the provider is a wiring bug, and the section
 * error boundary reports it (logger.error) instead of silently rendering nothing. */
export function useLandingBlocks(): LandingBlocksValue {
  const value = useContext(LandingBlocksContext);
  if (!value) throw new Error('LandingBlocksContext is missing: wrap SitePageRenderer in it');
  return value;
}

/** Page-level facts the renderer derives once and the blocks read. */
export interface SitePageMeta {
  /** The company widget rebuilt from the LEGACY_HTML sections THIS viewer can see. */
  widget: Widget;
  /** Whether a HERO this viewer can see is on the page; it consumes LEGACY_HTML TOP/BOTTOM. */
  hasHero: boolean;
  /**
   * Where the page came from. `PREVIEW` is the Back Office preview: LEAD_FORM validates but never
   * posts (PB-8615). Defaults to `PUBLISHED`.
   */
  source: SitePageSourceView;
  /**
   * The published page version (PB-8609): the announcement's dismissal is remembered per version.
   * `undefined` for a PREVIEW (never persisted) and for the client-composed FALLBACK page.
   */
  version?: number;
  /**
   * The modes the page's search panel (a SEARCH block, or else a HERO's embedded card) offers;
   * empty when the page has no search panel (`pageSearchModes`). BOOK / MONTHLY drive the panel
   * only for a mode listed here.
   */
  searchModes: readonly ReservationMode[];
  /** Whether a SEARCH block is on the page; the hero then embeds no search card. */
  hasSearchBlock: boolean;
  /**
   * The custom page's slug (PB-9146).
   *
   * ## Invariants:
   * - Absent means the homepage (or a Back Office preview of it). Only the custom page route
   *   sets it, so it is deliberately not in the default context value.
   * - The lead form sends it as `?slug=`, so the api can resolve the page's own LEAD_FORM.
   */
  slug?: string;
}

export const SitePageMetaContext = createContext<SitePageMeta>({
  widget: { widgetMap: {} },
  hasHero: false,
  source: 'PUBLISHED',
  searchModes: [],
  hasSearchBlock: false,
});

export function useSitePageMeta(): SitePageMeta {
  return useContext(SitePageMetaContext);
}
